package dev.lingvo.site.pages.app.start

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.COMMON_LOCALES
import dev.lingvo.shared.GitHubImportRequest
import dev.lingvo.shared.GitHubImportResultDto
import dev.lingvo.shared.GitHubScanDto
import dev.lingvo.shared.ProjectKind
import dev.lingvo.shared.localeFlagPrefix
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.loadAsync
import dev.lingvo.site.components.github.ModuleCard
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LCheckbox
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LStep
import dev.lingvo.site.ds.LSteps
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.enterRise
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.WorkspaceSelection
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch

/**
 * Importing from a repository: the steps on one route.
 *
 * One route with a step indicator rather than several routes, because back-navigation across routes
 * would re-mint the installation exchange and send somebody round the loop again.
 *
 * **The scope is stated before the install**, not after: read-only `contents:read`, no writes, no
 * pull requests. The person deciding is often not the person who can approve a GitHub App, and a
 * permission list that arrives after the decision is a request that gets refused.
 */
/**
 * Also reachable from a project that already exists.
 *
 * `?project=<slug>` imports into that project instead of creating one. Somebody who made a project
 * by hand, or with `lingvo init`, and then wants their repository's strings should not have to
 * create a second project for the same app — which was the only thing this page could do.
 */
@Page("/app/start/github")
@Layout(".components.layouts.AppLayout")
@Composable
fun GitHubImportPage() {
    val ctx = rememberPageContext()
    val intoProject = ctx.route.params["project"]?.takeIf(String::isNotBlank)
    val scope = rememberCoroutineScope()
    val redirectInstallationId = ctx.route.params["installation_id"].orEmpty()
    // GitHub hands both back on the redirect it was given. The state is what proves this
    // installation belongs to the workspace that asked for it, so it is redeemed before the page
    // tries to use the installation for anything.
    val installState = ctx.route.params["state"].orEmpty()
    var claimError by remember { mutableStateOf<String?>(null) }
    // Bumped by the retry below, so a connection check that failed can be asked again. Without one
    // `loadAsync` had nothing to re-key on, and the only retry available was a browser reload —
    // which replays the install redirect against a consumed state token and manufactures a second,
    // false failure on top of the first.
    var connectionAttempt by remember { mutableStateOf(0) }

    // **The id on the URL exists for exactly one page load.**
    //
    // Reading it there and nowhere else meant the app forgot the installation the moment somebody
    // navigated — so anybody who installed the app and then came back to this page was told to
    // install it again, with no way past a screen they had already completed. The connection is a
    // property of the workspace and is stored as one; this asks.
    val workspace = WorkspaceSelection.current()?.slug
    val connected = loadAsync(workspace, claimError, redirectInstallationId, connectionAttempt) {
        workspace?.let { ApiClient.gitHubInstallations(it).installationIds } ?: emptyList()
    }
    val connectedIds: List<Long> = (connected as? Async.Success)?.value.orEmpty()
    val redirectId = redirectInstallationId.toLongOrNull()

    // **A failed claim is two very different things, and the page was hiding the wrong one.**
    //
    // It used to drop any message containing "already connected", commented as "the ordinary second
    // visit". The only server response carrying that phrase is the 409 saying the installation
    // belongs to *another* workspace — which is the one failure after which `/repos`, `/scan` and
    // `/import` all answer 404 by design, because `requireInstallation` resolves the installation to
    // the workspace that claimed it. The ordinary second visit produces no error at all, and a
    // replayed link produces "expired or already been used", which that filter never matched.
    //
    // The honest test is not the wording. It is whether this workspace can see the installation once
    // the list has been read again: if it can, the claim had already been made here and the failure
    // is noise; if it cannot, nothing below this line will work and the reader has to be told.
    val claimBlocking = claimError != null &&
        connected is Async.Success &&
        (redirectId == null || redirectId !in connectedIds)

    val installationId = redirectInstallationId.takeIf { it.isNotBlank() && !claimBlocking }
        ?: connectedIds.firstOrNull()?.toString().orEmpty()

    // Still loading the answer is not the same as "no installation", and rendering the install
    // prompt while the question is in flight is the same false statement in a shorter window. A
    // claim that failed makes the id on the URL suspect too, so that case waits for the list as
    // well rather than walking into a step where every call answers 404.
    val installationUnknown = connected is Async.Loading &&
        (redirectInstallationId.isBlank() || claimError != null)
    // **A load that did not arrive is not an answer either.** Coerced through `as? Async.Success`,
    // a failed `/github/installations` read as "you have no installation" — so somebody who is
    // already connected was shown the full install card and sent round the GitHub loop again, under
    // copy promising that step would be skipped.
    val installationCheckFailed = connected as? Async.Failure

    // Loaded once the installation is known and claimed, so the picker below has something to show.
    val repos = loadAsync(installationId, claimError) {
        installationId.takeIf(String::isNotBlank)?.let { ApiClient.listGitHubRepos(it) } ?: emptyList()
    }
    LaunchedEffect(redirectInstallationId, installState) {
        if (redirectInstallationId.isBlank() || installState.isBlank()) return@LaunchedEffect
        redirectInstallationId.toLongOrNull()?.let { id ->
            claimError = try {
                ApiClient.claimGitHubInstallation(id, installState)
                null
            } catch (cancelled: CancellationException) {
                // Leaving the page is not a failed claim. Writing the cancellation's message into
                // `claimError` re-keyed both loads below it and drew "That install link could not be
                // confirmed" over an installation that was perfectly fine.
                throw cancelled
            } catch (failure: Throwable) {
                failure.message
                    ?: tr("app.start.github.the_connection_could_not_be_confirmed", "The connection could not be confirmed.")
            }
        }
    }

    var owner by remember { mutableStateOf("") }
    var repo by remember { mutableStateOf("") }
    var scan by remember { mutableStateOf<GitHubScanDto?>(null) }
    var projectName by remember { mutableStateOf("") }
    // The module view is the default; the exact paths are one button away, because the shape you
    // confirm should stay inspectable.
    var rawFiles by remember { mutableStateOf(false) }
    var imported by remember { mutableStateOf<GitHubImportResultDto?>(null) }
    var expandedModules by remember { mutableStateOf(emptySet<String>()) }
    var sourceLocale by remember { mutableStateOf("en") }
    var kind by remember { mutableStateOf(ProjectKind.OTHER) }
    var chosen by remember { mutableStateOf(emptySet<String>()) }
    var busy by remember { mutableStateOf(false) }
    // Unauthenticated and already cached by the client; it carries the app's public slug.
    val providers = loadAsync(Unit) { ApiClient.authProviders() }
    var message by remember { mutableStateOf<String?>(null) }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(
            t("app.start.github.import_from_github", "Import from GitHub"),
            // Three, counted honestly: installing is a step, and calling it "1 of 2" while showing
            // step zero meant somebody who installed out-of-band came back to "Step 1 of 2" again
            // and could not tell whether it had worked.
            // `{step}` rather than a Kotlin template: the extractor cannot key a string that is
            // built by interpolation, so the page's own subtitle sat outside the catalogue and no
            // locale could ever render it. The placeholder is the same contract `tPlural` uses.
            t(
                "app.start.github.nothing_is_created_until_you_confirm",
                "Nothing is created until you confirm.",
            ),
        )

        // **The count was a sentence, and a sentence cannot be scanned.** "Step 2 of 3" told
        // somebody where they were only if they read it; the three names tell them what is left.
        // Completion comes from the set rather than from position, which is what makes an
        // out-of-band install land on step 2 with step 1 already done rather than at the start.
        run {
            val current = if (installationId.isBlank()) 0 else if (scan == null) 1 else 2
            LSteps(
                steps = listOf(
                    LStep(
                        t("app.start.github.connect", "Connect"),
                        t("app.start.github.install_the_app_on_the_repositories", "Install the app on the repositories you pick."),
                    ),
                    LStep(
                        t("app.start.github.choose_repository", "Choose repository"),
                        t("app.start.github.point_at_the_one_holding_your", "Point at the one holding your localization files."),
                    ),
                    LStep(
                        t("app.start.github.confirm", "Confirm"),
                        t("app.start.github.nothing_is_written_until_this_step", "Nothing is written until this step."),
                    ),
                ),
                current = current,
                completed = (0 until current).toSet(),
                label = t("app.start.github.import_from_github", "Import from GitHub"),
            )
        }

        // **A GitHub installation is connected to a workspace, and this account is in none.**
        // Without a workspace this page still drew the whole install card, and pressing its button
        // asked `/github/install-url?orgSlug=` — which `requireOrgAccess` answers "Workspace not
        // found.", a sentence about a workspace to somebody who has none, under a heading offering
        // to connect one. `/app` and `/app/start` both answer this case with the action that fixes
        // it.
        if (workspace == null) {
            LEmptyState(
                title = t("app.start.github.you_are_not_in_a_workspace", "You are not in a workspace yet"),
                description = t("app.start.github.a_github_installation_belongs_to_a", "A GitHub installation belongs to a workspace, and this account is in none. " +
                    "Make one first and the import will have somewhere to put the project."),
                action = {
                    LButton(
                        label = t("app.start.github.create_a_workspace", "Create a workspace"),
                        onClick = { ctx.router.tryRoutingTo("/app/workspaces") },
                        variant = LButtonVariant.PRIMARY,
                    )
                },
            )
            return@Column
        }

        // `live`, because every one of these is set by a button handler — the post-action notice the
        // house rule reserves the live region for.
        message?.let {
            LAlert(
                t("app.start.github.that_didnt_work", "That didn't work"),
                it,
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }
        // Told, and then given the button. The first version of this said "start the connection
        // again from this page" while the page below it had already moved on to step 2 — an
        // instruction with nothing to press, which is the failure this whole audit kept finding.
        if (claimBlocking) {
            claimError?.let {
                LAlert(
                    t("app.start.github.that_install_link_could_not_be", "That install link could not be confirmed"),
                    it,
                    variant = LAlertVariant.WARNING,
                )
            }
            // Our sentence, beside the server's, rather than spliced into it: a translated title
            // over an English body assembled with `$it` is a string no catalogue can hold.
            LMuted(
                t("app.start.github.the_link_github_sends_you_back", "The link GitHub sends you back with is single-use and lasts half an hour. " +
                    "Your app is still installed — this only re-establishes which workspace it " +
                    "belongs to."),
            )
            GitHubInstallButton(
                label = t("app.start.github.start_the_connection_again", "Start the connection again"),
                busy = busy,
                onBusy = { busy = it },
                onMessage = { message = it },
            )
        }

        if (installationUnknown) {
            LoadingState(rows = 1, label = t("app.start.github.checking_your_github_connection", "Checking your GitHub connection"))
            // **Waiting has to actually wait.** Without this return the skeleton rendered *above*
            // the step-2 repository picker rather than instead of it: the branch below is entered
            // whenever `scan` is null, which it always is here. So the reader was shown "Checking
            // your GitHub connection" and, underneath it, an owner/name form whose "Look at it"
            // called `scanRepository("")` — the exact walk-into-step-2 the comment on
            // `installationUnknown` says this case avoids.
            return@Column
        } else if (installationId.isBlank() && installationCheckFailed != null) {
            LAlert(
                t("app.start.github.we_could_not_check_your_github", "We could not check your GitHub connection"),
                installationCheckFailed.message,
                variant = LAlertVariant.WARNING,
                action = {
                    LButton(
                        t("app.start.github.retry", "Retry"),
                        { connectionAttempt++ },
                        variant = LButtonVariant.SECONDARY,
                        size = LButtonSize.SM,
                    )
                },
            )
            LMuted(
                t("app.start.github.if_you_have_already_installed_the", "If you have already installed the app, retrying is all this needs — it does " +
                    "not install a second copy, and nothing has been created."),
            )
            return@Column
        } else if (installationId.isBlank()) {
            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                    // A heading, not bold text. Every step title on this page was a `Span`, so the
                    // heading outline was the `H1` followed by whichever alerts happened to be
                    // showing — a screen reader could jump between the warnings and nothing else.
                    LSectionTitle(t("app.start.github.connect_lingvo_to_github", "Connect Lingvo to GitHub"))
                    // Before the click, in plain words. This is the paragraph somebody forwards to
                    // whoever actually administers their GitHub organisation.
                    LMuted(
                        t("app.start.github.lingvo_asks_for_read_access_to_2", "Lingvo asks for read access to repository contents and nothing else. It " +
                            "cannot write to your code, cannot open pull requests, and cannot see " +
                            "anything outside the repositories you pick during install."),
                    )
                    LMuted(t("app.start.github.you_choose_the_repositories_on_githubs", "You choose the repositories on GitHub's own screen — Lingvo never asks for all of them."))
                    // **For the person who reads this having already installed it.**
                    //
                    // The connection is a link between a GitHub installation and *this workspace*,
                    // and the app being installed is only half of it. Somebody whose first attempt
                    // did not finish — an expired link, a closed tab — comes back to this screen and
                    // reasonably reads it as "install it again", which is not what the button does
                    // and not what GitHub will show them.
                    LMuted(
                        t("app.start.github.already_installed_it_use_the_same_2", "Already installed it? Use the same button. GitHub will recognise the app, " +
                            "ask which repositories this workspace may read, and send you straight " +
                            "back here — it does not install a second copy."),
                    )

                    // **The button this page did not have.**
                    //
                    // It explained the permissions it wanted, in copy written to be forwarded to
                    // whoever administers the organisation, and then offered no way to grant them.
                    // The only route past this screen was to arrive already holding an
                    // `installation_id` in the URL, which nothing in the product ever produced.
                    //
                    // Where the URL comes from, and why it is a button rather than an anchor, is on
                    // [GitHubInstallButton]. The paragraph that used to sit here described an
                    // anchor this code has never rendered.
                    //
                    // **"Not configured" is a claim, and it was being made from an unanswered
                    // question.** `(providers as? Async.Success)?.value?.githubAppSlug == null`
                    // collapses Loading and Failure into the same branch, so every reader was told
                    // on first paint that this deployment cannot import from GitHub — and told it
                    // permanently after any transient `/auth/providers` failure, with no retry, on
                    // the terminal screen of the door `/app/start` had just advertised.
                    when (val configured = providers) {
                        is Async.Loading -> LoadingState(
                            rows = 1,
                            label = t("app.start.github.checking_this_deployments_github_app", "Checking this deployment's GitHub app"),
                        )
                        is Async.Success -> if (configured.value.githubAppSlug == null) {
                            LMuted(
                                t("app.start.github.import_from_github_is_not_configured_2", "Import from GitHub is not configured on this deployment, so there is " +
                                    "nothing to install. Use the file import or the CLI instead."),
                            )
                        } else {
                            GitHubInstallButton(
                                label = t("app.start.github.continue_on_github", "Continue on GitHub"),
                                busy = busy,
                                onBusy = { busy = it },
                                onMessage = { message = it },
                            )
                        }
                        // Unknown, so the button stays. The install URL is minted by the server,
                        // which is the authority on whether this deployment has an app at all; a
                        // press it refuses reports the real reason above, which is a better answer
                        // than a page inventing one from a request that never arrived.
                        is Async.Failure -> GitHubInstallButton(
                            label = t("app.start.github.continue_on_github", "Continue on GitHub"),
                            busy = busy,
                            onBusy = { busy = it },
                            onMessage = { message = it },
                        )
                    }
                    LMuted(
                        t("app.start.github.github_sends_you_back_here_afterwards_2", "GitHub sends you back here afterwards. If it does not, come back to this " +
                            "page — the install is already done and this step will be skipped."),
                    )
                }
            }
            return@Column
        }

        if (scan == null) {
            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                    LSectionTitle(t("app.start.github.which_repository", "Which repository?"))

                    // Read here rather than inside the option map below: `t` is `@Composable` and
                    // the map's lambda is not a composable scope.
                    val privateSuffix = t("app.start.github.private", "private")

                    // **The list, not two empty boxes.** The install screen on GitHub is where
                    // somebody picked these repositories, minutes earlier; asking them to retype the
                    // owner and name from memory — exactly, or the scan 404s — is asking them to do
                    // the server's work. `/github/repos/{id}` has always returned this and nothing
                    // called it.
                    when (val available = repos) {
                        Async.Loading -> LoadingState(rows = 2, label = t("app.start.github.loading_your_repositories", "Loading your repositories"))
                        is Async.Failure -> {
                            // Two lines rather than one sentence with the server's message spliced
                            // into it: interpolation is what kept this string out of the catalogue.
                            LMuted(
                                t("app.start.github.we_could_not_list_your_repositories", "We could not list your repositories. Type the owner and name below " +
                                    "instead."),
                            )
                            LMuted(available.message)
                        }
                        is Async.Success -> if (available.value.isEmpty()) {
                            LMuted(
                                t("app.start.github.this_installation_can_see_no_repositories_2", "This installation can see no repositories. Add one to it on GitHub, " +
                                    "or type the owner and name below."),
                            )
                        } else {
                            LSelect(
                                value = "$owner/$repo".takeIf { owner.isNotBlank() && repo.isNotBlank() }.orEmpty(),
                                onValueChange = { picked ->
                                    owner = picked.substringBefore('/')
                                    repo = picked.substringAfter('/')
                                },
                                modifier = Modifier.fillMaxWidth(),
                                label = t("app.start.github.repository", "Repository"),
                                options = listOf(LSelectOption("", t("app.start.github.choose_a_repository", "Choose a repository"))) +
                                    available.value.map { entry ->
                                        LSelectOption(  // i18n-ignore — the option's value is an identifier; its label beside it is translated
                                            "${entry.owner}/${entry.name}",
                                            trf(
                                                "app.start.github.repo_option_label",
                                                "{owner}/{name}{visibility}",
                                                "owner" to entry.owner,
                                                "name" to entry.name,
                                                "visibility" to if (entry.private) " · $privateSuffix" else "",
                                            ),
                                        )
                                    },
                            )
                        }
                    }

                    // Still typeable. A monorepo somebody granted at the org level may not be in the
                    // list, and an install that granted "all repositories" can be long enough that
                    // typing is faster than scrolling.
                    Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.Bottom) {
                        LTextField(owner, { owner = it }, Modifier.flexGrow(1), label = t("app.start.github.owner", "Owner"), placeholder = "acme")
                        LTextField(repo, { repo = it }, Modifier.flexGrow(1), label = t("app.start.github.repository", "Repository"), placeholder = "mobile")
                    }
                    LButton(
                        t("app.start.github.look_at_it", "Look at it"),
                        {
                            scope.launch {
                                busy = true
                                // Not `runCatching`: `scanRepository` suspends, and `runCatching`
                                // swallows the CancellationException this scope throws when the page
                                // leaves composition — which reports a failure that never happened
                                // and leaves the button spinning.
                                try {
                                    val found = ApiClient.scanRepository(installationId, owner.trim(), repo.trim())
                                    scan = found
                                    projectName = repo.trim()
                                    sourceLocale = found.sourceLocale ?: "en"
                                    kind = found.kind
                                    chosen = found.files.map { it.path }.toSet()
                                    message = null
                                } catch (cancelled: CancellationException) {
                                    throw cancelled
                                } catch (failure: Throwable) {
                                    message = failure.message
                                        ?: tr("app.start.github.check_your_connection_and_retry", "Check your connection and retry.")
                                } finally {
                                    busy = false
                                }
                            }
                        },
                        enabled = owner.isNotBlank() && repo.isNotBlank() && !busy,
                        loading = busy,
                    )
                    LMuted(t("app.start.github.nothing_is_created_by_looking_this", "Nothing is created by looking. This only reads the file list."))
                }
            }
            return@Column
        }

        imported?.let { result ->
            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                    // Hoisted out of the `Text(...)`: a conditional inside an argument position is
                    // invisible to the string extractor, so both of these sat outside the catalogue.
                    val resultTitle = if (result.created) {
                        t("app.start.github.project_created", "Project created")
                    } else {
                        t("app.start.github.files_imported", "Files imported")
                    }
                    LSectionTitle(resultTitle)
                    // Grouped by language, because that is the unit somebody checks: the source
                    // should hold every string, and each translation however many it had.
                    result.files
                        .filter { it.error == null }
                        .groupBy { it.locale }
                        .entries
                        .sortedByDescending { entry -> entry.value.sumOf { it.added + it.updated } }
                        .forEach { (locale, files) ->
                            val info = localeInfo(locale)
                            val landed = files.sumOf { it.added + it.updated }
                            val skipped = files.sumOf { it.skipped }
                            // `"$n string" + if (n == 1) "" else "s"` is English's plural rule under
                            // a general-sounding name, on the page whose own product sells "real
                            // plurals". Polish needs a third form at five and Japanese needs one.
                            LMuted(
                                buildString {
                                    append(localeFlagPrefix(info.code))
                                    append(info.englishName)
                                    append(" — ")
                                    append(tPlural(
                                        "app.start.github.strings_landed",
                                        landed,
                                        "{count} string",
                                        "{count} strings",
                                    ))
                                    if (skipped > 0) {
                                        append(", ")
                                        append(
                                            tPlural(
                                                "app.start.github.strings_skipped",
                                                skipped,
                                                "{count} skipped (no matching key)",
                                                "{count} skipped (no matching key)",
                                            ),
                                        )
                                    }
                                },
                            )
                        }
                    result.files.filter { it.error != null }.forEach { file ->
                        LAlert(file.path, file.error.orEmpty(), variant = LAlertVariant.WARNING)
                    }
                    LButton(t("app.start.github.open_the_project", "Open the project"), { ctx.router.tryRoutingTo(result.next) })
                }
            }
            return@Column
        }

        val found = scan!!
        // Arrives in response to Scan, replacing the form that was there, after a network round
        // trip somebody waited through. The rise says this card is the answer to what they just
        // did rather than an unrelated page — the same argument `settings/tokens/New.kt` makes for
        // a created token. The modules inside it deliberately stay still: they arrive *with* this
        // card, and a second entrance nested in a rising surface just doubles the travel.
        LCard(Modifier.fillMaxWidth().enterRise()) {
            Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                // **Two flows, not one wearing the other's clothes.** Importing into a project that
                // already exists is a different act from creating one: its name is decided, its
                // source language is decided — and offering to change that here is offering to
                // retag every string in the project as another language — and nothing is being
                // created, so a button saying "Create the project" is describing something else.
                val confirmTitle = if (intoProject != null) {
                    t("app.start.github.check_this_before_anything_is_imported", "Check this before anything is imported")
                } else {
                    t("app.start.github.check_this_before_anything_is_created", "Check this before anything is created")
                }
                LSectionTitle(confirmTitle)

                // The evidence beside the guess. "KMP" on its own is something somebody accepts on
                // faith, and this is the step where a wrong guess is still cheap to fix.
                //
                // The kind is named, not spelled from its enum constant: `kind.name.lowercase()`
                // put "Looks like kmp" and "Looks like ios" on screen, which is the serialization
                // format leaking into the one sentence that is supposed to be checkable.
                val kindLabel = when (found.kind) {
                    ProjectKind.KMP -> t("app.start.github.kotlin_multiplatform", "Kotlin Multiplatform")
                    ProjectKind.ANDROID -> t("app.start.github.android", "Android")
                    ProjectKind.IOS -> t("app.start.github.ios", "iOS")
                    ProjectKind.WEB -> t("app.start.github.web", "Web")
                    ProjectKind.OTHER -> t("app.start.github.other", "Other")
                }
                found.kindEvidence?.let { evidence ->
                    LMuted(
                        tf(
                            "app.start.github.looks_like_kind_because_of_evidence",
                            "Looks like {kind}, because of {evidence}",
                            "kind" to kindLabel,
                            "evidence" to evidence,
                        ),
                    )
                }

                if (intoProject == null) {
                    LTextField(projectName, { projectName = it }, Modifier.fillMaxWidth(), label = t("app.start.github.project_name", "Project name"))
                } else {
                    LMuted(
                        tf(
                            "app.start.github.these_files_are_being_added_to",
                            "These files are being added to {project}. Strings it already has are " +
                            "updated; new ones are added. Nothing is deleted.", "project" to intoProject),
                    )
                }
                if (intoProject == null) {
                    // **A picker, not a text box.** This is the one answer on the page that cannot be
                    // walked back — getting it wrong tags every source string as the wrong language —
                    // and free text invited "de-DE", "german", "DE", "eng", each of which imports
                    // happily and wrongly. The blank-project path has always used a picker for exactly
                    // this field; this one did not, and it is the path where the value arrives
                    // pre-filled by detection and therefore reads as already confirmed.
                    LSelect(
                        value = sourceLocale,
                        options = COMMON_LOCALES.map {
                            LSelectOption(it.code, "${localeFlagPrefix(it.code)}${it.englishName} — ${it.code}")  // i18n-ignore — a flag glyph immediately followed by a language name — no separator, no words, nothing a translator could change
                        },
                        onValueChange = { sourceLocale = it },
                        label = t("app.start.github.source_language", "Source language"),
                    )
                    // An alert rather than muted grey. The comment here used to say "loud, because
                    // getting it wrong is nearly unrecoverable" above the quietest text on the page.
                    LAlert(
                        t("app.start.github.check_this_one_carefully", "Check this one carefully"),
                        // Wrapped, because a translated title over an English body is what
                        // `LAlert(t(…), "literal")` produces: the extractor only reads argument #2
                        // when argument #1 is a plain literal, so wrapping the title is exactly what
                        // made the body invisible. This is the warning the file itself calls nearly
                        // unrecoverable.
                        t("app.start.github.the_language_your_source_strings_are", "The language your source strings are written in — the originals, not a " +
                            "translation. Everything else on this page can be changed afterwards; " +
                            "this cannot, without re-importing."),
                        variant = LAlertVariant.WARNING,
                    )
                }

                // **Modules first, paths behind them.** The scanner produces paths; nobody holds
                // their own repository as a list of paths. They hold it as "the widget, the iOS
                // app, the shared Compose code", and they open this screen asking which of those
                // is behind in which language. That question used to be answerable only by reading
                // forty directory names.
                val contentsLabel = if (found.modules.isEmpty()) {
                    tf("app.start.github.files_count", "Files ({count})", "count" to found.files.size.toString())
                } else {
                    tPlural("app.start.github.modules", found.modules.size, "{count} module", "{count} modules")
                }
                val viewToggleLabel = if (rawFiles) {
                    t("app.start.github.group_by_module", "Group by module")
                } else {
                    t("app.start.github.show_every_file", "Show every file")
                }
                Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                    LSectionTitle(contentsLabel, Modifier.fontSize(TypeScale.BodySmall))
                    Spacer()
                    if (found.files.isNotEmpty()) {
                        LButton(
                            viewToggleLabel,
                            { rawFiles = !rawFiles },
                            variant = LButtonVariant.GHOST,
                            size = LButtonSize.SM,
                        )
                    }
                }

                if (found.modules.isNotEmpty() && !rawFiles) {
                    found.modules.forEach { module ->
                        ModuleCard(
                            module = module,
                            sourceLocale = sourceLocale,
                            chosen = chosen,
                            // Keyed by id *and* subtitle: one directory can hold two source sets
                            // — `composeApp` commonMain and androidMain — and keying on the
                            // directory alone made both cards expand and collapse together.
                            expanded = "${module.id}/${module.subtitle}" in expandedModules,
                            onToggleExpanded = {
                                val key = "${module.id}/${module.subtitle}"
                                expandedModules = if (key in expandedModules) {
                                    expandedModules - key
                                } else {
                                    expandedModules + key
                                }
                            },
                            onToggleModule = { on ->
                                chosen = if (on) chosen + module.paths else chosen - module.paths.toSet()
                            },
                            onTogglePath = { path, on ->
                                chosen = if (on) chosen + path else chosen - path
                            },
                        )
                    }
                }
                // **A scan that finds nothing has to say so.** It used to render "Files (0)" above
                // a Create button, which makes an empty project and looks like the scan failed.
                // Some repositories genuinely have no localization files — a service, a library,
                // an app whose strings are still hardcoded — and that is an answer, not an error.
                if (found.files.isEmpty()) {
                    LAlert(
                        t("app.start.github.no_localization_files_found_in_this", "No localization files found in this repository"),
                        t("app.start.github.lingvo_looks_for_android_and_compose", "Lingvo looks for Android and Compose Resources strings and plurals under " +
                            "values/, Apple .xcstrings and .lproj catalogues, JSON, YAML, gettext " +
                            ".po and XLIFF under locales/ or i18n/, and Java resource bundles. If " +
                            "your strings are still hardcoded in source, there is nothing to import " +
                            "yet — create an empty project and push with the CLI instead."),
                        variant = LAlertVariant.WARNING,
                    )
                }
                if (found.modules.isEmpty() || rawFiles) {
                    found.files.forEach { file ->
                        LCheckbox(
                            checked = file.path in chosen,
                            onCheckedChange = { on -> chosen = if (on) chosen + file.path else chosen - file.path },
                            label = tf(
                                "app.start.github.file_summary",
                                "{path} · {format}{locale}",
                                "path" to file.path,
                                "format" to file.format.displayName,
                                "locale" to file.locale?.let { " · $it" }.orEmpty(),
                            ),
                        )
                    }
                }

                // What was deliberately left out. Without this, a repository whose only string file
                // sits in `values-night/` looks to its owner like a repository Lingvo cannot read.
                if (found.skipped.isNotEmpty()) {
                    LSectionTitle(
                        tf(
                            "app.start.github.left_out_count",
                            "Left out ({count})",
                            "count" to found.skipped.size.toString(),
                        ),
                        Modifier.fontSize(TypeScale.BodySmall),
                    )
                    found.skipped.forEach { skipped ->
                        LMuted(tf("app.start.github.skipped_and_reason", "{path} — {reason}", "path" to skipped.path, "reason" to skipped.reason))
                    }
                }

                val confirmLabel = if (intoProject == null) {
                    t("app.start.github.create_the_project", "Create the project")
                } else {
                    t("app.start.github.import_these_files", "Import these files")
                }
                Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                    LButton(
                        confirmLabel,
                        {
                            scope.launch {
                                busy = true
                                // Not `runCatching`: `importFromGitHub` suspends, and swallowing the
                                // CancellationException of leaving the page reports a failure that
                                // never happened over an import that may well have landed.
                                try {
                                    val result = ApiClient.importFromGitHub(
                                        installationId,
                                        GitHubImportRequest(
                                            owner = owner.trim(),
                                            repo = repo.trim(),
                                            ref = "HEAD",
                                            projectName = projectName.trim(),
                                            sourceLocale = sourceLocale.trim(),
                                            kind = kind,
                                            paths = chosen.toList(),
                                            // Set when this page was opened from an existing
                                            // project. Null on the onboarding path, which creates
                                            // one.
                                            projectSlug = intoProject,
                                        ),
                                    )
                                    // The list the switcher caches just changed.
                                    dev.lingvo.site.state.ProjectDirectory.invalidate()
                                    // What landed, not how many files were fetched. A repository
                                    // whose files all failed to parse used to route straight to a
                                    // green project page with nothing in it.
                                    val failed = result.files.filter { it.error != null }
                                    message = if (result.added == 0 && result.updated == 0) {
                                        val reason = failed.firstOrNull()?.error
                                            ?: tr("app.start.github.none_of_the_chosen_files_held", "None of the chosen files held strings Lingvo could read.")
                                        trf(
                                            "app.start.github.nothing_was_imported_reason",
                                            "Nothing was imported. {reason}",
                                            "reason" to reason,
                                        )
                                    } else {
                                        // **Say what landed, per language, before leaving.** The
                                        // page routed straight to the project on success, so the
                                        // one moment that could answer "how many strings arrived in
                                        // the source, and how many in each translation" passed
                                        // without showing it — and the project page reports state,
                                        // not what this import did.
                                        imported = result
                                        null
                                    }
                                } catch (cancelled: CancellationException) {
                                    throw cancelled
                                } catch (failure: Throwable) {
                                    message = failure.message
                                        ?: tr("app.start.github.check_your_connection_and_retry", "Check your connection and retry.")
                                } finally {
                                    busy = false
                                }
                            }
                        },
                        // A name is only required where one is being chosen.
                        enabled = (intoProject != null || projectName.isNotBlank()) && chosen.isNotEmpty() && !busy,
                        loading = busy,
                    )
                    // **The way back this step never had.** `scan` was assigned in exactly one place
                    // and cleared nowhere, so somebody who scanned the wrong repository — a typo in
                    // the owner, the wrong entry in a long list — had no control on the page that
                    // could undo it. The only exit was a browser reload, which replays the install
                    // redirect against a consumed state token and answers "that install link has
                    // expired", inventing a second failure out of a typo.
                    LButton(
                        t("app.start.github.choose_another_repository", "Choose another repository"),
                        {
                            scan = null
                            chosen = emptySet()
                            expandedModules = emptySet()
                            rawFiles = false
                            message = null
                        },
                        variant = LButtonVariant.GHOST,
                        enabled = !busy,
                    )
                }
            }
        }
    }
}

/**
 * The button that leaves for GitHub, in the two places this page offers it.
 *
 * **The URL is minted by the server, not assembled here.** It carries a state token tied to this
 * person and this workspace, which is the only thing that makes the installation id GitHub hands
 * back on the return mean anything: that id is a public identifier, and when the routes trusted it
 * on its own, any account could name somebody else's installation and read their private
 * repositories.
 *
 * One composable rather than two copies, because the copies had drifted into two different failure
 * paths for the same request.
 */
@Composable
private fun GitHubInstallButton(
    label: String,
    busy: Boolean,
    onBusy: (Boolean) -> Unit,
    onMessage: (String?) -> Unit,
) {
    val scope = rememberCoroutineScope()
    LButton(
        label,
        {
            scope.launch {
                onBusy(true)
                // Not `runCatching`: `gitHubInstallUrl` suspends, and `runCatching` swallows the
                // CancellationException thrown when this button leaves composition — which reports
                // a failure that never happened and leaves the button spinning.
                try {
                    val target = ApiClient.gitHubInstallUrl(WorkspaceSelection.current()?.slug.orEmpty())
                    onMessage(null)
                    window.location.href = target.url
                } catch (cancelled: CancellationException) {
                    throw cancelled
                } catch (failure: Throwable) {
                    onMessage(
                        failure.message
                            ?: tr("app.start.github.check_your_connection_and_retry", "Check your connection and retry."),
                    )
                } finally {
                    onBusy(false)
                }
            }
        },
        enabled = !busy,
        loading = busy,
    )
}

